import { MapPin, Calendar, Ruler, User } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const Meta = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
    <Icon size={13} className="text-sky-600" />
    {children}
  </span>
);

const TechChips = ({ items, testid }) => (
  <div className="mt-5 flex flex-wrap gap-2" data-testid={testid}>
    {items.map((item) => (
      <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
        {item}
      </span>
    ))}
  </div>
);

const Projects = () => {
  const { t } = useLanguage();
  const list = t("projects.list");
  const featured = list[0];
  const projects = list.slice(1);

  return (
    <section id="prosjekter" className="scroll-mt-20 border-t border-slate-200 bg-slate-50 py-20 sm:py-28" data-testid="projects-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700" data-testid="projects-badge">
            {t("projects.badge")}
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="projects-title">
            {t("projects.title")}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
            {t("projects.intro")}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <article
            className="group grid overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg lg:grid-cols-2"
            data-testid={`project-card-${featured.id}`}
          >
            <div className="relative overflow-hidden">
              <img
                src={featured.image}
                alt={featured.name}
                className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105 lg:h-full"
                data-testid={`project-image-${featured.id}`}
              />
              <span className="absolute left-4 top-4 rounded-full border border-white/40 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-800 backdrop-blur-sm">
                {featured.badge}
              </span>
              <span className="absolute bottom-2 right-3 text-[10px] text-white/80 drop-shadow">{featured.credit}</span>
            </div>
            <div className="flex flex-col justify-center p-7 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-700">{t("projects.featuredLabel")}</p>
              <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">{featured.name}</h3>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5" data-testid={`project-meta-${featured.id}`}>
                <Meta icon={User}>{featured.client}</Meta>
                <Meta icon={MapPin}>{featured.location}</Meta>
                <Meta icon={Calendar}>{featured.year}</Meta>
                <Meta icon={Ruler}>{featured.size}</Meta>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{featured.scope}</p>
              <p className="mt-3 border-l-2 border-sky-600 pl-4 text-sm font-medium leading-relaxed text-slate-700">
                {featured.role}
              </p>
              <TechChips items={featured.tech} testid={`project-tech-${featured.id}`} />
            </div>
          </article>
        </Reveal>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <article
                className="group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg"
                data-testid={`project-card-${p.id}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-60 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-64"
                    data-testid={`project-image-${p.id}`}
                  />
                  <span className="absolute left-4 top-4 rounded-full border border-white/40 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-800 backdrop-blur-sm">
                    {p.badge}
                  </span>
                  <span className="absolute bottom-2 right-3 text-[10px] text-white/80 drop-shadow">{p.credit}</span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5" data-testid={`project-meta-${p.id}`}>
                    <Meta icon={User}>{p.client}</Meta>
                    <Meta icon={MapPin}>{p.location}</Meta>
                    <Meta icon={Calendar}>{p.year}</Meta>
                    <Meta icon={Ruler}>{p.size}</Meta>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.scope}</p>
                  <p className="mt-3 border-l-2 border-sky-600 pl-4 text-sm font-medium leading-relaxed text-slate-700">
                    {p.role}
                  </p>
                  <TechChips items={p.tech} testid={`project-tech-${p.id}`} />
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
